<template>
  <RevForm
    :initial-values
    name="report-illicit-content"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <div class="space-y-32">
        <RevStepper
          :active-step="currentStep"
          :alternative-text-back="i18n(translations.stepperAltBack)"
          :alternative-text-close="i18n(translations.stepperAltClose)"
          :alternative-text-completed="i18n(translations.stepperAltCompleted)"
          :alternative-text-current="i18n(translations.stepperAltCurrent)"
          :steps="stepperSteps"
        />

        <!-- Step 1 — The illicit content -->
        <ReportIllicitContentStepContent
          v-if="currentStep === STEP_NAMES.CONTENT"
          v-model:files="files"
          :disabled="isSubmitting"
          :errors
          :model-value="values"
          :risk-nature-options
          :yes-no-unknown-options
        />

        <!-- Step 2 — Your details -->
        <ReportIllicitContentStepAbout
          v-else-if="currentStep === STEP_NAMES.ABOUT"
          :capacity-options
          :errors
          :model-value="values"
        />

        <!-- Step 3 — Declaration -->
        <ReportIllicitContentStepDeclaration
          v-else
          :errors
          :model-value="values"
        />

        <!-- Navigation -->
        <div class="flex gap-12">
          <RevButton
            v-if="!isFirstStep"
            :disabled="isSubmitting"
            type="button"
            variant="secondary"
            @click="goToPreviousStep"
          >
            {{ i18n(translations.back) }}
          </RevButton>

          <RevButton
            v-if="!isLastStep"
            :disabled="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.continue) }}
          </RevButton>

          <RevButton
            v-else
            :disabled="isSubmitting"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submit) }}
          </RevButton>
        </div>
      </div>
    </template>
  </RevForm>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { FileUploadError } from '@backmarket/nuxt-module-upload/FileUploadError'
import { getUploadErrorMessage } from '@backmarket/nuxt-module-upload/getUploadErrorMessage'
import { RevButton } from '@ds/components/Button'
import { RevForm } from '@ds/components/Form'
import { RevStepper } from '@ds/components/Stepper'

import type {
  ReportIllicitContentAttachment,
  ReportIllicitContentData,
} from '~/api/care-platform/types/HelpCenterForms.types'

import {
  CONDITIONAL_CATEGORIES,
  STEP_NAMES,
} from '../../ReportIllicitContent.constants'
import type { ReportIllicitContentFormValues } from '../../ReportIllicitContent.types'
import { useReportIllicitContentEvidenceUpload } from '../../composables/useReportIllicitContentEvidenceUpload'
import { useReportIllicitContentForm } from '../../composables/useReportIllicitContentForm'
import { useReportIllicitContentSteps } from '../../composables/useReportIllicitContentSteps'

import translations from './ReportIllicitContentForm.translations'
import ReportIllicitContentStepAbout from './steps/ReportIllicitContentStepAbout.vue'
import ReportIllicitContentStepContent from './steps/ReportIllicitContentStepContent.vue'
import ReportIllicitContentStepDeclaration from './steps/ReportIllicitContentStepDeclaration.vue'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { openErrorToast } = useTheToast()
const logger = useLogger()
const emit = defineEmits<{ success: [] }>()
const isSubmitting = ref(false)
const files = ref<File[]>([])

const { uploadEvidence } = useReportIllicitContentEvidenceUpload()

const {
  currentStep,
  stepperSteps,
  isFirstStep,
  isLastStep,
  goToPreviousStep,
  goToNextStep,
} = useReportIllicitContentSteps()

const {
  initialValues,
  validate,
  capacityOptions,
  riskNatureOptions,
  yesNoUnknownOptions,
} = useReportIllicitContentForm(currentStep)

// Resolves with null when the upload failed: the error toast is already shown.
async function uploadAttachments(): Promise<
  ReportIllicitContentAttachment[] | null
> {
  try {
    return await uploadEvidence(files.value)
  } catch (error) {
    const isFileUploadError = error instanceof FileUploadError

    // File names can contain PII: only log the upload step and reason.
    logger.error('[ReportIllicitContent] Evidence upload failed', {
      owners: ['bot-squad-care-platform-front'],
      step: isFileUploadError ? error.step : 'unknown',
      reason: isFileUploadError ? error.reason : 'unknown',
    })

    openErrorToast({
      title: i18n(translations.uploadErrorTitle),
      content: isFileUploadError
        ? i18n(getUploadErrorMessage(error))
        : i18n(translations.uploadErrorDescription),
    })

    return null
  }
}

async function handleSubmit(values: ReportIllicitContentFormValues) {
  if (isSubmitting.value) return

  if (!isLastStep.value) {
    goToNextStep()

    return
  }

  if (!values.category || !values.capacity) return

  isSubmitting.value = true

  const {
    category,
    description,
    contentUrl,
    reporterName,
    reporterEmail,
    capacity,
    country,
    goodFaithCertified,
    isTrustedFlagger,
    riskNature,
    hasCausedIncident,
    isSubjectToRecall,
  } = values
  const hasConditionalFields = (
    CONDITIONAL_CATEGORIES as readonly string[]
  ).includes(category)
  const data: ReportIllicitContentData = {
    category,
    description,
    contentUrl,
    reporterName,
    reporterEmail,
    reporterCapacity: capacity,
    countryOfResidence: country,
    goodFaithCertified,
    trustedFlagger: isTrustedFlagger,
    ...(hasConditionalFields && riskNature && { riskNature }),
    ...(hasConditionalFields && hasCausedIncident && { hasCausedIncident }),
    ...(hasConditionalFields && isSubjectToRecall && { isSubjectToRecall }),
  }

  try {
    if (files.value.length > 0) {
      const attachmentIds = await uploadAttachments()

      if (!attachmentIds) return

      data.attachmentIds = attachmentIds
    }

    await $httpFetch('/api/care-platform/external/v1/help-center/forms', {
      method: 'POST',
      body: { formType: 'dsa_illicit_content', data },
      retry: 0,
    })

    emit('success')
  } catch (error) {
    const status =
      error instanceof Error &&
      'status' in error &&
      typeof error.status === 'number'
        ? error.status
        : undefined

    if (status !== 400 && status !== 429) {
      // Do not log the error object: failed requests can contain reporter PII.
      logger.error('[ReportIllicitContent] Submission failed', {
        owners: ['bot-squad-care-platform-front'],
        status: status ?? 'network_or_unknown',
      })
    }

    openErrorToast({
      title: i18n(translations.submitErrorTitle),
      content: i18n(translations.submitErrorDescription),
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
